import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getCaseFileUrl } from '../../services/storageService';
import { getImageAnnotations } from '../../services/annotationService';
import { createSimilarCase } from '../../services/caseService';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ReportHeader from './components/ReportHeader';
import ReportSidebar from './components/ReportSidebar';
import SectionContent from './components/SectionContent';
import AnnotatedImage from './components/AnnotatedImage';
import NavigationControls from './components/NavigationControls';
import SearchBar from './components/SearchBar';
import ReportDisclaimer from './components/ReportDisclaimer';
import ExpertMarketplaceCTA from './components/ExpertMarketplaceCTA';
import toast from 'react-hot-toast';

const ReportViewer = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [report, setReport] = useState(null);
  const [images, setImages] = useState([]);
  const [currentSection, setCurrentSection] = useState(0);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [creatingRevision, setCreatingRevision] = useState(false);

  useEffect(() => {
    fetchReportData();
  }, [caseId]);

  const fetchReportData = async () => {
    try {
      setLoading(true);

      const { data: caseRecord, error: caseError } = await supabase?.from('cases')?.select('*')?.eq('id', caseId)?.single();

      if (caseError) throw caseError;
      setCaseData(caseRecord);

      const { data: reportRecord, error: reportError } = await supabase?.from('reports')?.select('*')?.eq('case_id', caseId)?.single();

      if (reportError) throw reportError;
      setReport(reportRecord);

      // Get PDF download URL if available
      if (reportRecord?.pdf_url) {
        const pdfResult = await getCaseFileUrl(reportRecord?.pdf_url, 'report-pdfs');
        if (pdfResult?.success) {
          setPdfDownloadUrl(pdfResult?.url);
        }
      }

      const { data: imageRecords, error: imageError } = await supabase?.from('case_images')?.select('*')?.eq('case_id', caseId)?.order('display_order', { ascending: true });

      if (imageError) throw imageError;

      const imagesWithUrls = await Promise.all(
        imageRecords?.map(async (img) => {
          const originalUrl = await getCaseFileUrl(img?.image_url, 'case-images');
          let annotatedUrl = null;
          
          if (img?.annotated_image_url) {
            const annotatedResult = await getCaseFileUrl(img?.annotated_image_url, 'annotated-images');
            if (annotatedResult?.success) {
              annotatedUrl = annotatedResult?.url;
            }
          }

          const annotationsResult = await getImageAnnotations(img?.id);

          return {
            ...img,
            url: originalUrl?.success ? originalUrl?.url : null,
            annotatedUrl,
            annotations: annotationsResult?.success ? annotationsResult?.data : [],
            alt: img?.caption || 'Fire scene evidence'
          };
        })
      );

      setImages(imagesWithUrls?.filter(img => img?.url));
    } catch (error) {
      console.error('Fetch report error:', error);
      toast?.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (pdfDownloadUrl) {
      window.open(pdfDownloadUrl, '_blank');
      toast?.success('PDF download started');
    } else {
      toast?.error('PDF not available');
    }
  };

  const handleCreateSimilarCase = async () => {
    if (!user || !caseId) return;

    try {
      setCreatingRevision(true);
      const result = await createSimilarCase(caseId, user?.id);

      if (!result?.success) {
        throw new Error(result?.error);
      }

      toast?.success('Revision case created! Redirecting to case wizard...');
      navigate(`/new-case-creation-wizard?revisionOf=${caseId}`);
    } catch (error) {
      console.error('Create similar case error:', error);
      toast?.error('Failed to create revision case');
    } finally {
      setCreatingRevision(false);
    }
  };

  const toggleAnnotations = () => {
    setShowAnnotations(!showAnnotations);
  };

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading report...</p>
        </div>
      </div>
    );
  }

  if (!report || !caseData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Report Not Found</h2>
          <p className="text-muted-foreground mb-6">The requested report could not be loaded.</p>
          <Button onClick={() => navigate('/client-dashboard')}>Return to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ReportHeader 
        caseNumber={caseData?.case_number}
        incidentDate={caseData?.incident_date}
        generatedDate={report?.created_at}
        onDownloadPDF={handleDownloadPDF}
        pdfAvailable={!!pdfDownloadUrl}
        onToggleSidebar={handleToggleSidebar}
        isSidebarCollapsed={isSidebarCollapsed}
      />

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <ReportSidebar
              sections={report?.report_data?.sections || []}
              currentSection={currentSection}
              activeSection={currentSection}
              onSectionChange={setCurrentSection}
              isCollapsed={isSidebarCollapsed}
            />
          </div>

          <div className="lg:col-span-3 space-y-6">
            <ReportDisclaimer />

            <div className="flex items-center gap-4 flex-wrap">
              <SearchBar 
                value={searchQuery} 
                onChange={setSearchQuery}
                onSearch={setSearchQuery}
              />
            </div>

            <SectionContent
              section={{
                ...report?.report_data?.sections?.[currentSection],
                ocrResults: currentSection === 4 ? [] : []
              }}
              images={images}
              annotations={showAnnotations}
              searchQuery={searchQuery}
              onImageClick={(imageId) => console.log('Image clicked:', imageId)}
            />

            {images?.length > 0 && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold">Evidence Images</h3>
                {images?.map((image, index) => (
                  <div key={image?.id || index} className="space-y-2">
                    <AnnotatedImage
                      image={{
                        url: showAnnotations && image?.annotatedUrl ? image?.annotatedUrl : image?.url,
                        alt: image?.alt,
                        caption: image?.caption
                      }}
                      annotations={showAnnotations ? image?.annotations : []}
                      showAnnotations={showAnnotations}
                    />
                    {image?.ai_description && (
                      <div className="p-4 rounded-lg bg-muted border border-border">
                        <p className="text-sm font-medium mb-1">AI Analysis:</p>
                        <p className="text-sm text-muted-foreground">{image?.ai_description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <NavigationControls
              currentSection={currentSection}
              totalSections={report?.report_data?.sections?.length || 0}
              onPrevious={() => setCurrentSection(Math.max(0, currentSection - 1))}
              onNext={() => setCurrentSection(Math.min(report?.report_data?.sections?.length - 1, currentSection + 1))}
            />

            <ExpertMarketplaceCTA caseId={caseId} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportViewer;